import { ChangeDetectionStrategy, Component, computed, input, output, signal } from "@angular/core";
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { MatFormField, MatLabel, MatPrefix } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { MatOption } from "@angular/material/core";
import { TINTABLE_ICON_KEYS } from "../../shared/icons/svg-icons-dictionary";

/**
 * Searchable picker over the project's curated `TINTABLE_ICON_KEYS` — a small subset of
 * `SVG_ICONS_DICTIONARY` restricted to simple, single-flat-color pin/marker icons known to tint
 * reliably (see `svg-icons-dictionary.ts`). Previews render via `<mat-icon [svgIcon]="key">` — every
 * dictionary entry is already registered with `MatIconRegistry` by `IconsModule` (shared/icons/icons.module.ts),
 * so no manual sanitizing/`[innerHTML]` is needed here.
 */
@Component({
  selector: "lib-icon-style-picker",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatAutocomplete, MatAutocompleteTrigger, MatFormField, MatIcon, MatLabel, MatInput, MatOption, MatPrefix],
  templateUrl: "./icon-style-picker.component.html",
  styleUrl: "./icon-style-picker.component.scss",
})
export class IconStylePickerComponent {
  readonly selectedKey = input<string | undefined>(undefined);
  /** Current icon tint color, if any — applied to the selected-icon preview and dropdown previews. */
  readonly tintColor = input<string | undefined>(undefined);

  readonly keySelected = output<string>();

  private readonly allKeys = [...TINTABLE_ICON_KEYS].sort();

  protected readonly searchText = signal("");

  protected readonly filteredKeys = computed(() => {
    const query = this.searchText().toLowerCase();
    return this.allKeys.filter((key) => key.toLowerCase().includes(query));
  });

  protected readonly displayValue = computed(() => this.selectedKey() ?? this.searchText());

  protected onSearchInput(raw: string): void {
    this.searchText.set(raw);
  }

  protected onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.keySelected.emit(event.option.value);
  }
}
